import { DEFAULT_PROJECTS_SORT_BY } from "@components/projects/projects-sort-constants";
import { isProjectsSortBy } from "@components/projects/projects-sort-utils";
import { PaginatedProjects } from "@custom-types/project-types";
import { useFetchNextProjectsPage } from "@hooks/projects/use-fetch-next-projects-page";
import { useFetchProjectsOnQueryChange } from "@hooks/projects/use-fetch-projects-on-query-change";
import { useProjectsSearchQuery } from "@hooks/projects/use-projects-search-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  fetchingProjectsFlagsSelector,
  projectsListSelector,
} from "@store/projects/projects-selector";
import { getListType } from "@store/projects/projects-slice-utils";
import { useAppSelector } from "@store/store-helper";

type ReturnProps = Pick<
  PaginatedProjects,
  | "projects"
  | "isFetchingProjects"
  | "hasFetchedFirstPage"
  | "nextPageCursor"
  | "sortBy"
  | "setSortBy"
  | "search"
  | "isDebouncingSearch"
  | "loadMoreProjects"
>;

type Props = Pick<
  PaginatedProjects,
  "companyId" | "projectArchivingState" | "groupId"
>;

/** Custom hook that handles fetching projects */
export function useFetchProjects({
  companyId,
  projectArchivingState,
  groupId,
}: Props): ReturnProps {
  const { isFetchingProjects } = useAppSelector(fetchingProjectsFlagsSelector);
  const { search, isDebouncingSearch } = useProjectsSearchQuery();
  const { sortBy, setSortBy } =
    useSubjectsSortByQuery<FaroCoreApiTypes.ProjectsSortBy>({
      defaultSort: DEFAULT_PROJECTS_SORT_BY,
      isValidSort: isProjectsSortBy,
    });
  const listType = getListType({
    companyId,
    projectArchivingState,
    search,
    sortBy,
    groupId,
  });
  const { projects, nextPageCursor, hasFetchedFirstPage } = useAppSelector(
    projectsListSelector(listType)
  );

  /** Fetches projects on changes in the search or sortBy values */
  useFetchProjectsOnQueryChange({
    companyId,
    projectArchivingState,
    sortBy,
    search,
    groupId,
  });

  /** Fetches the next page of projects */
  const loadMoreProjects = useFetchNextProjectsPage({
    companyId,
    projectArchivingState,
    sortBy,
    nextPageCursor,
    search,
    groupId,
  });

  return {
    projects,
    isFetchingProjects,
    hasFetchedFirstPage,
    nextPageCursor,
    sortBy,
    setSortBy,
    search,
    isDebouncingSearch,
    loadMoreProjects,
  };
}
